// src/pages/Subscriptions.js
//
// Page dédiée au suivi des abonnements (mensuels/semestriels, Mollie ou
// virement/espèces) ET des cartes 10 cours — vue complémentaire à
// Registrations.js, centrée sur "qui doit payer/venir bientôt" plutôt que
// sur le détail de chaque inscription.
//
// Cadence des abonnements mensuels = MOIS CALENDAIRE (même jour du mois,
// plafonné au 28), pas 4 semaines fixes : c'est ce que prélève réellement
// Mollie (createPayment / mollieWebhook / chargeRecurringInstallments dans
// admin_panel/functions/index.js), donc la date affichée ici correspond
// exactement à la date du prochain prélèvement. Cette page ne fait aucun
// calcul de cadence propre — elle lit installmentsPaid/installmentsTotal/
// nextPaymentDate/subscriptionStatus, déjà écrits par les Cloud Functions.
//
// Deux onglets :
//  • Abonnements : installmentsTotal > 0 (mensuel classique + Formation Crew).
//  • Cartes 10 cours : plan === 'carte10', avec date d'achat et crédits restants.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, FormControl, InputLabel, Select,
  Chip, LinearProgress, CircularProgress, Grid, AppBar, Toolbar, IconButton,
  ToggleButton, ToggleButtonGroup, Tooltip, Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  EventRepeat as EventRepeatIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
moment.locale('fr');

// ── Helpers (mêmes conventions que Registrations.js) ────────────────────────

const readDate = (date) => {
  if (!date) return null;
  if (date?.toDate) return date.toDate();
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Date "jour" : gère les chaînes ISO 'YYYY-MM-DD' (nextPaymentDate,
// subscriptionStartDate écrites par les Cloud Functions) en heure LOCALE —
// new Date('2026-11-05') serait interprétée en UTC et pourrait tomber la veille.
const readDay = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return readDate(value);
};

const formatDay = (value) => {
  const d = readDay(value);
  return d ? moment(d).format('DD/MM/YYYY') : '—';
};

const money = (value) => {
  const n = Number(value || 0);
  return `${n.toFixed(Number.isInteger(n) ? 0 : 2)} €`;
};

const isPaidStatus = (status) =>
  ['paid', 'payé', 'paye'].includes((status || '').toLowerCase());

const displayName = (reg) => {
  const persons = reg.persons || [];
  if (persons.length > 0) return persons.map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim()).join(', ');
  return reg.targetStudentName || (reg.contactInfo && reg.contactInfo.email) || '—';
};

// Délai de grâce : un prélèvement SEPA peut rester "pending" quelques jours
// avant d'être confirmé — on ne parle de retard qu'au-delà.
const OVERDUE_GRACE_DAYS = 5;

const SUB_STATUS = {
  pending_mandate: { label: 'Mandat en attente',  bg: '#fff3e0', color: '#e65100' },
  active:          { label: 'Actif',              bg: '#e8f5e9', color: '#2e7d32' },
  payment_failed:  { label: 'Prélèvement échoué', bg: '#ffebee', color: '#c62828' },
  mandate_failed:  { label: 'Mandat refusé',      bg: '#ffebee', color: '#c62828' },
  completed:       { label: 'Terminé',            bg: '#eceff1', color: '#455a64' },
};
const SUB_OVERDUE = { label: 'En retard', bg: '#ffebee', color: '#c62828' };

const FREQUENCY_LABELS = {
  monthly:   'Mensuel',
  semester:  'Semestriel',
  semester1: 'Semestre 1',
  semester2: 'Semestre 2',
  annual:    'Annuel',
};

const PAYMENT_MODE_LABELS = { mollie: 'Mollie (auto)', manual: 'Virement / espèces' };

const getSubscriptionInfo = (d) => {
  const total = Number(d.installmentsTotal || 0);
  const paid  = Number(d.installmentsPaid || 0);
  const status = d.subscriptionStatus || null;
  const next = readDay(d.nextPaymentDate);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const limit = new Date(today); limit.setDate(limit.getDate() - OVERDUE_GRACE_DAYS);
  const overdue = status === 'active' && next !== null && next < limit;
  const now = new Date();
  const dueSoon = status === 'active' && next !== null
    && next >= today && next <= new Date(today.getTime() + 14 * 86400000);

  const meta = overdue ? SUB_OVERDUE : (SUB_STATUS[status] || { label: status || '—', bg: '#f5f5f5', color: '#616161' });

  return { total, paid, status, next, overdue, dueSoon, label: meta.label, bg: meta.bg, color: meta.color };
};

const StatusChip = ({ label, bg, color }) => (
  <Chip label={label} size="small" sx={{ backgroundColor: bg, color, fontWeight: 600 }} />
);

// ── Composant ────────────────────────────────────────────────────────────

function Subscriptions() {
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('subscriptions'); // 'subscriptions' | 'cards'
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subs, setSubs]         = useState([]);
  const [cards, setCards]       = useState([]);
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'registrations'));
        const subsData = [];
        const cardsData = [];
        const unmatched = []; // diagnostic : ressemble à un abonnement mais pas reconnu comme tel

        snap.forEach((docSnap) => {
          const d = docSnap.data();
          if (d.status === 'cancelled') return;

          // installmentsTotal > 0 couvre les abonnements passés par
          // createPayment/le trigger de suivi. On inclut AUSSI toute
          // inscription avec une fréquence récurrente même sans
          // installmentsTotal (inscriptions antérieures au suivi, ou créées
          // par un chemin qui ne l'a pas initialisé) — l'annuel reste exclu
          // car c'est un paiement unique, pas un abonnement à échéances.
          const recurringFrequency = ['monthly', 'semester', 'semester1', 'semester2'].includes(d.paymentFrequency);
          if (Number(d.installmentsTotal) > 0 || recurringFrequency) {
            subsData.push({
              id: docSnap.id,
              name: displayName(d),
              planName: d.planName || d.plan || '—',
              frequency: FREQUENCY_LABELS[d.paymentFrequency] || d.paymentFrequency || '—',
              startDate: d.subscriptionStartDate || d.subscriptionAnchorDate || null,
              paymentMode: PAYMENT_MODE_LABELS[d.paymentMode] || (d.paymentMode ? d.paymentMode : '—'),
              installmentAmount: d.installmentAmount,
              sub: getSubscriptionInfo(d),
              paymentStatus: d.paymentStatus,
              contactEmail: d.contactInfo?.email || '',
            });
          } else if (d.plan === 'carte10') {
            const total = Number(d.creditsTotal || 10);
            const used  = Number(d.creditsUsed || 0);
            cardsData.push({
              id: docSnap.id,
              name: displayName(d),
              purchasedAt: d.createdAt || null,
              creditsTotal: total,
              creditsUsed: used,
              creditsRemaining: Math.max(total - used, 0),
              paymentStatus: d.paymentStatus,
              contactEmail: d.contactInfo?.email || '',
            });
          } else {
            // Diagnostic : ni carte 10, ni reconnue comme abonnement — mais
            // ressemble à un abonnement mensuel/semestriel par un autre
            // signal. Aide à repérer le nom exact du champ/de la valeur
            // réellement utilisé si le critère ci-dessus ne matche pas.
            const looksLikeSubscription = /abonnement|mensuel|monthly/i.test(String(d.plan || '') + String(d.planName || ''))
              || d.installmentAmount != null
              || d.subscriptionStartDate != null
              || d.mollieCustomerId != null;
            if (looksLikeSubscription) {
              unmatched.push({
                id: docSnap.id,
                name: displayName(d),
                plan: d.plan,
                planName: d.planName,
                paymentFrequency: d.paymentFrequency,
                installmentsTotal: d.installmentsTotal,
                subscriptionStartDate: d.subscriptionStartDate,
                installmentAmount: d.installmentAmount,
                paymentMode: d.paymentMode,
              });
            }
          }
        });

        if (unmatched.length > 0) {
          console.warn(
            `Subscriptions.js : ${unmatched.length} inscription(s) ressemblent à un abonnement mais ne sont pas reconnues (champs à vérifier : plan/paymentFrequency/installmentsTotal). Détail :`,
            unmatched
          );
        }
        setUnmatchedCount(unmatched.length);

        setSubs(subsData);
        setCards(cardsData);
      } catch (e) {
        console.error('Erreur chargement abonnements:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filteredSubs = useMemo(() => {
    const s = search.trim().toLowerCase();
    return subs
      .filter((r) => !s || r.name.toLowerCase().includes(s) || r.contactEmail.toLowerCase().includes(s))
      .filter((r) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'overdue') return r.sub.overdue;
        if (statusFilter === 'dueSoon') return r.sub.dueSoon;
        if (statusFilter === 'failed')  return r.sub.status === 'payment_failed' || r.sub.status === 'mandate_failed';
        if (statusFilter === 'active')  return r.sub.status === 'active' && !r.sub.overdue;
        if (statusFilter === 'completed') return r.sub.status === 'completed';
        return true;
      })
      .sort((a, b) => {
        // En retard d'abord, puis par prochaine échéance croissante
        if (a.sub.overdue !== b.sub.overdue) return a.sub.overdue ? -1 : 1;
        const an = a.sub.next ? a.sub.next.getTime() : Infinity;
        const bn = b.sub.next ? b.sub.next.getTime() : Infinity;
        return an - bn;
      });
  }, [subs, search, statusFilter]);

  const filteredCards = useMemo(() => {
    const s = search.trim().toLowerCase();
    return cards
      .filter((r) => !s || r.name.toLowerCase().includes(s) || r.contactEmail.toLowerCase().includes(s))
      .sort((a, b) => a.creditsRemaining - b.creditsRemaining);
  }, [cards, search]);

  const overdueCount = subs.filter((r) => r.sub.overdue).length;
  const failedCount  = subs.filter((r) => r.sub.status === 'payment_failed' || r.sub.status === 'mandate_failed').length;

  return (
    <Box sx={{ p: 0 }}>
      <AppBar position="static" elevation={0} sx={{ backgroundColor: '#7C3AED', mb: 3 }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <EventRepeatIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Gestion des abonnements
          </Typography>
          {(overdueCount > 0 || failedCount > 0) && (
            <Tooltip title={`${overdueCount} en retard, ${failedCount} en échec de prélèvement`}>
              <Chip
                icon={<WarningIcon sx={{ color: '#fff !important' }} />}
                label={overdueCount + failedCount}
                size="small"
                sx={{ backgroundColor: '#c62828', color: '#fff', fontWeight: 700 }}
              />
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            exclusive
            value={tab}
            onChange={(e, val) => { if (val) setTab(val); }}
            size="small"
          >
            <ToggleButton value="subscriptions" sx={{ px: 2 }}>
              Abonnements {subs.length > 0 && `(${subs.length})`}
            </ToggleButton>
            <ToggleButton value="cards" sx={{ px: 2 }}>
              Cartes 10 cours {cards.length > 0 && `(${cards.length})`}
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder="Rechercher un nom ou un e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
          />

          {tab === 'subscriptions' && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Statut</InputLabel>
              <Select label="Statut" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="overdue">En retard</MenuItem>
                <MenuItem value="dueSoon">Échéance sous 14 jours</MenuItem>
                <MenuItem value="failed">Prélèvement / mandat échoué</MenuItem>
                <MenuItem value="active">Actifs (à jour)</MenuItem>
                <MenuItem value="completed">Terminés</MenuItem>
              </Select>
            </FormControl>
          )}
        </Paper>

        {unmatchedCount > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {unmatchedCount} inscription{unmatchedCount > 1 ? 's ressemblent' : ' ressemble'} à un abonnement
            mais n'{unmatchedCount > 1 ? 'ont' : 'a'} pas pu être reconnue{unmatchedCount > 1 ? 's' : ''} automatiquement.
            Détail dans la console du navigateur (touche F12, onglet « Console »).
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : tab === 'subscriptions' ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Élève</strong></TableCell>
                  <TableCell><strong>Formule</strong></TableCell>
                  <TableCell><strong>Fréquence</strong></TableCell>
                  <TableCell><strong>Début</strong></TableCell>
                  <TableCell><strong>Mode</strong></TableCell>
                  <TableCell><strong>Échéances</strong></TableCell>
                  <TableCell><strong>Prochaine échéance</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucun abonnement pour ces filtres.
                    </TableCell>
                  </TableRow>
                )}
                {filteredSubs.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/registrations', { state: { openRegistrationId: r.id } })}
                  >
                    <TableCell>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>{r.contactEmail}</div>
                    </TableCell>
                    <TableCell>{r.planName}</TableCell>
                    <TableCell>{r.frequency}</TableCell>
                    <TableCell>{formatDay(r.startDate)}</TableCell>
                    <TableCell>{r.paymentMode}</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      {r.sub.total > 0 && (
                        <>
                          <div>{r.sub.paid}/{r.sub.total}{r.installmentAmount ? ` · ${money(r.installmentAmount)}` : ''}</div>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((r.sub.paid / r.sub.total) * 100, 100)}
                            sx={{ mt: 0.5, height: 6, borderRadius: 4, backgroundColor: '#eee',
                                  '& .MuiLinearProgress-bar': { backgroundColor: '#7C3AED' } }}
                          />
                        </>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: r.sub.overdue ? '#c62828' : 'inherit', fontWeight: r.sub.overdue ? 700 : 400 }}>
                      {r.sub.status === 'completed' ? '—' : formatDay(r.sub.next)}
                    </TableCell>
                    <TableCell>
                      <StatusChip {...r.sub} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Élève</strong></TableCell>
                  <TableCell><strong>Date d'achat</strong></TableCell>
                  <TableCell><strong>Crédits consommés</strong></TableCell>
                  <TableCell><strong>Crédits restants</strong></TableCell>
                  <TableCell><strong>Paiement</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCards.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucune carte 10 cours pour ces filtres.
                    </TableCell>
                  </TableRow>
                )}
                {filteredCards.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/registrations', { state: { openRegistrationId: r.id } })}
                  >
                    <TableCell>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>{r.contactEmail}</div>
                    </TableCell>
                    <TableCell>{formatDay(r.purchasedAt)}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <div>{r.creditsUsed}/{r.creditsTotal}</div>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((r.creditsUsed / r.creditsTotal) * 100, 100)}
                        sx={{ mt: 0.5, height: 6, borderRadius: 4, backgroundColor: '#eee',
                              '& .MuiLinearProgress-bar': { backgroundColor: r.creditsRemaining <= 2 ? '#e65100' : '#7C3AED' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.creditsRemaining}
                        size="small"
                        color={r.creditsRemaining === 0 ? 'default' : r.creditsRemaining <= 2 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={isPaidStatus(r.paymentStatus) ? 'Payé' : 'À payer'}
                        bg={isPaidStatus(r.paymentStatus) ? '#e8f5e9' : '#fff3e0'}
                        color={isPaidStatus(r.paymentStatus) ? '#2e7d32' : '#e65100'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

export default Subscriptions;